import { DialogTitle, Dialog, DialogActions, DialogContent, DialogContentText } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowBack, MdRefresh, MdDownload, MdFileDownload } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Button } from "rsuite";
import * as XLSX from "xlsx";
import axios from "axios";
import { web_url } from "../../constant";

function SchoolLetterReport() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [isExcelLoading, setIsExcelLoading] = useState(false);
    const [statsData, setStatsData] = useState([]);
    const [selectedRound, setSelectedRound] = useState("all");
    const requestId = useRef(0);
    const roundLabel = selectedRound === "all" ? "All" : `Round ${selectedRound}`;
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [openError, setOpenError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const handleCloseError = () => {
        setOpenError(false);
    };

    const getToken = () => {
        const logDetails =
            JSON.parse(localStorage.getItem("logDetails")) ||
            JSON.parse(sessionStorage.getItem("logDetails"));
        return logDetails?.token;
    };

    const fetchStats = useCallback((isRefresh = false) => {
        const id = ++requestId.current;
        const token = getToken();
        if (!token) return;
        isRefresh ? setIsRefreshing(true) : setIsLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` }, params: { round: selectedRound } };
        axios
            .get(`${web_url}/api/email-threads/school-letter-stats`, config)
            .then((res) => {
                if (id !== requestId.current) return;
                setStatsData(res.data);
                setSelectedSchool(previous => res.data.some(s => s.schoolName === previous) ? previous : res.data[0]?.schoolName || null);
            })
            .catch(() => {
                if (id !== requestId.current) return;
                setErrorMsg("Failed to load school letter statistics. Please try again.");
                setOpenError(true);
            })
            .finally(() => {
                if (id !== requestId.current) return;
                setIsRefreshing(false);
                setIsLoading(false);
            });
    }, [selectedRound]);

    useEffect(() => {
        fetchStats(false);
        return () => { requestId.current++; };
    }, [fetchStats]);

    const currentSchoolData = statsData.find((s) => s.schoolName === selectedSchool) || null;

    const exportExcel = () => {
        if (!currentSchoolData) return;
        const wb = XLSX.utils.book_new();

        const submittedRows = currentSchoolData.studentsSubmitted.map((s) => ({
            "Student Name": s.studentName,
            "Student Email": s.studentEmail,
            "Letters Submitted": s.letterCount,
        }));
        const ws1 = XLSX.utils.json_to_sheet(
            submittedRows.length > 0 ? submittedRows : [{ "Student Name": "No submissions yet", "Student Email": "", "Letters Submitted": 0 }]
        );
        XLSX.utils.book_append_sheet(wb, ws1, "Submitted Students");

        const notSubmittedRows = currentSchoolData.studentsNotSubmitted.map((student) => ({
            "Student Name": student.studentName || student.studentEmail,
            "Student Email": student.studentEmail,
        }));
        const ws2 = XLSX.utils.json_to_sheet(
            notSubmittedRows.length > 0 ? notSubmittedRows : [{ "Student Name": "All students have submitted" }]
        );
        XLSX.utils.book_append_sheet(wb, ws2, "Not Submitted");

        const summaryRows = [
            { Metric: "School", Value: currentSchoolData.schoolName },
            { Metric: "Round", Value: roundLabel },
            { Metric: "Total Letters Uploaded", Value: currentSchoolData.totalLetters },
            { Metric: "Students Submitted", Value: currentSchoolData.studentsSubmitted.length },
            { Metric: "Students Not Submitted", Value: currentSchoolData.studentsNotSubmitted.length },
        ];
        const ws3 = XLSX.utils.json_to_sheet(summaryRows);
        XLSX.utils.book_append_sheet(wb, ws3, "Summary");

        XLSX.writeFile(wb, `${currentSchoolData.schoolName.replace(/\s+/g, "_")}_round_${selectedRound}_letter_report.xlsx`);
    };

    const downloadPdf = () => {
        if (!selectedSchool || !currentSchoolData) return;
        const token = getToken();
        if (!token) return;
        
        // Open the print window synchronously to prevent popup blockers
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            setErrorMsg("Please allow popups to download the PDF.");
            setOpenError(true);
            return;
        }
        printWindow.document.write("<html><head><title>Loading Report...</title></head><body><h2 style='font-family:sans-serif;text-align:center;margin-top:50px;color:#7A5E00;'>Generating Report...</h2></body></html>");

        setIsPdfLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const allStudents = [
            ...(currentSchoolData.studentsSubmitted || []),
            ...(currentSchoolData.studentsNotSubmitted || [])
        ];
        const emailToName = {};
        allStudents.forEach(s => { emailToName[s.studentEmail] = s.studentName || s.studentEmail; });

        axios.get(`${web_url}/api/email-threads/school-letters`, {
            ...config, params: { school: selectedSchool, round: selectedRound }
        }).then((res) => {
            const letters = res.data;
            if (!letters.length) {
                printWindow.close(); setIsPdfLoading(false);
                setErrorMsg("No received letters for this school and round."); setOpenError(true);
                return;
            }


            const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
            const formatDate = (ts) => ts ? new Date(ts).toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

            const detectLanguage = (text) => {
                if (!text) return "";
                let sinhalaCount = 0;
                let tamilCount = 0;
                for (let i = 0; i < text.length; i++) {
                    const code = text.charCodeAt(i);
                    if (code >= 0x0D80 && code <= 0x0DFF) sinhalaCount++;
                    if (code >= 0x0B80 && code <= 0x0BFF) tamilCount++;
                }
                if (sinhalaCount > tamilCount) return "Sinhala";
                if (tamilCount > sinhalaCount) return "Tamil";
                return "";
            };

            let bodyContent = "";
            letters.forEach((msg, i) => {
                const originalBody = stripHtml(msg.body);
                const englishBody = stripHtml(msg.englishBody);
                const translatedBody = stripHtml(msg.translatedBody);
                
                const detectedOriginalLang = detectLanguage(msg.body) || msg.language || "";
                const detectedTransLang = detectLanguage(msg.translatedBody) || msg.language || "";

                bodyContent += `
                    <div class="letter" style="${i > 0 ? 'page-break-before: always;' : ''}">
                        <h2 style="font-size:16px;color:#A07B00;margin-bottom:8px;">Letter ${i + 1}</h2>
                        <table style="font-size:12px;margin-bottom:12px;border-collapse:collapse;">
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">From:</td><td>${msg.sender}</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">To:</td><td>${emailToName[msg.receiver] || msg.receiver} (${msg.receiver})</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">Date:</td><td>${formatDate(msg.sentAt)}</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">Status:</td><td>${msg.status || ""}</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">Language:</td><td>${detectedOriginalLang}</td></tr>
                        </table>
                        ${originalBody ? `<div style="margin-bottom:14px;"><p style="font-size:13px;font-weight:bold;color:#1A5276;margin-bottom:4px;">Original Letter (${detectedOriginalLang || "Sinhala/Tamil"}):</p><div style="font-size:12px;background:#F8F9FA;border-left:3px solid #1A5276;padding:10px 14px;line-height:1.7;white-space:pre-wrap;">${originalBody}</div></div>` : ""}
                        ${translatedBody ? `<div style="margin-bottom:14px;"><p style="font-size:13px;font-weight:bold;color:#C0392B;margin-bottom:4px;">Translated Version (${detectedTransLang || "Sinhala/Tamil"}):</p><div style="font-size:12px;background:#FFF5F5;border-left:3px solid #C0392B;padding:10px 14px;line-height:1.7;white-space:pre-wrap;">${translatedBody}</div></div>` : ""}
                    </div>
                `;
            });

            const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>School Letter Report - ${selectedSchool} - ${roundLabel}</title>
<style>
@font-face { font-family: 'Noto Sans Sinhala'; src: url('${window.location.origin}/fonts/NotoSansSinhala-Regular.ttf'); }
@font-face { font-family: 'Noto Sans Tamil'; src: url('${window.location.origin}/fonts/NotoSansTamil-Regular.ttf'); }
body { font-family: 'Noto Sans', 'Noto Sans Sinhala', 'Noto Sans Tamil', Arial, sans-serif; padding: 20px; color: #333; }
.letter { page-break-inside: avoid; }
</style></head><body>
<h1 style="font-size:22px;color:#7A5E00;border-bottom:2px solid #FDC50C;padding-bottom:10px;">School Letter Report — ${selectedSchool} — ${roundLabel}</h1>
<p style="font-size:13px;color:#666;">Total letters: ${letters.length} | Generated: ${new Date().toLocaleString()}</p>
<hr style="border:1px solid #eee;margin:20px 0;" />
${bodyContent}
<script>document.fonts.ready.then(function(){ window.print(); });<\/script>
</body></html>`;

            if (printWindow) {
                printWindow.document.open();
                printWindow.document.write(fullHtml);
                printWindow.document.close();
            }
            setIsPdfLoading(false);
        }).catch(() => {
            printWindow.close();
            setErrorMsg("Failed to generate the PDF. Please try again.");
            setOpenError(true);
            setIsPdfLoading(false);
        });
    };

    const downloadExcelFull = () => {
        if (!selectedSchool) return;
        const token = getToken();
        if (!token) return;
        setIsExcelLoading(true);
        const config = {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
            params: { round: selectedRound },
        };
        axios
            .get(`${web_url}/api/email-threads/school-letters-excel?school=${encodeURIComponent(selectedSchool)}`, config)
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `${selectedSchool.replace(/\s+/g, "_")}_round_${selectedRound}_letters.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            })
            .catch(() => {
                setErrorMsg("Failed to generate the Excel file. Please try again.");
                setOpenError(true);
            })
            .finally(() => {
                setIsExcelLoading(false);
            });
    };

    return (
        <>
            <div className={`${i18n.language === "en" ? "font-urban" : i18n.language === "si" ? "font-noto" : "font-mukta"} overflow-x-hidden xl:pt-[20px] min-h-screen pb-[120px]`}>
                <div className="pt-[100px] md:pt-[130px] lg:pt-[150px] xl:pt-[0px] w-full flex flex-wrap gap-3 justify-between items-center pr-[20px] md:pr-[60px] xl:pr-[80px]">
                    <div className="ml-[30px] md:ml-[60px] lg:ml-[80px] xl:ml-[80px] flex items-center">
                        <MdArrowBack size={25} className="md:hidden cursor-pointer" onClick={() => navigate("/mobile/researcher/dashboard")} />
                        <MdArrowBack size={30} className="hidden md:block xl:hidden cursor-pointer" onClick={() => navigate("/mobile/researcher/dashboard")} />
                        <MdArrowBack size={30} className="hidden xl:block cursor-pointer" onClick={() => navigate("/desktop/researcher/dashboard")} />
                        <p className="text-[20px] md:text-[23px] lg:text-[25px] xl:text-[20px] 2xl:text-[22px] font-bold px-[10px] py-[5px] md:px-[13px] md:py-[8px]">
                            School Letter Report
                        </p>
                    </div>
                    <button
                        id="refresh-counts-btn"
                        disabled={isRefreshing || isLoading || isPdfLoading || isExcelLoading}
                        onClick={() => fetchStats(true)}
                        className="ml-[30px] md:ml-[60px] xl:ml-0 flex shrink-0 items-center gap-[6px] bg-[#FDC50C] text-black rounded-md text-[13px] md:text-[15px] py-[5px] px-[10px] hover:bg-opacity-80 disabled:opacity-50"
                    >
                        <MdRefresh size={18} className={isRefreshing ? "animate-spin" : ""} />
                        Refresh Counts
                    </button>
                </div>

                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-5">
                    <label htmlFor="school-report-round" className="block text-sm md:text-base font-semibold mb-2">Select Round</label>
                    <select id="school-report-round" value={selectedRound} disabled={isPdfLoading || isExcelLoading}
                        onChange={e => { setStatsData([]); setSelectedRound(e.target.value); }}
                        className="w-full md:w-[400px] max-w-full border border-gray-300 rounded-md p-3 bg-white text-gray-800 focus:ring-2 focus:ring-[#FDC50C]">
                        <option value="all">All</option><option value="1">Round 1</option><option value="2">Round 2</option>
                    </select>
                    <p className="text-xs md:text-sm text-gray-600 mt-2">Round 1: through 6 September 2026. Round 2: from 7 September 2026 (Sri Lanka time).</p>
                    <p className="text-xs md:text-sm text-gray-600 mt-1">Counts show letters sent by students. Downloads contain letters received by the selected school.</p>
                </div>
                {isLoading ? (
                    <div className="flex justify-center items-center pt-[60px]">
                        <div className="w-10 h-10 border-2 border-t-transparent border-[#FFA803] rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <>
                        <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[20px]">
                            <label className="block text-[14px] md:text-[16px] font-semibold text-gray-700 mb-[6px]">
                                Select School
                            </label>
                            <div className="relative w-full md:w-[400px]">
                                <input
                                    type="text"
                                    disabled={isPdfLoading || isExcelLoading}
                                    value={isDropdownOpen ? searchTerm : (selectedSchool || "")}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setIsDropdownOpen(true);
                                    }}
                                    onFocus={(e) => {
                                        setSearchTerm(selectedSchool || "");
                                        setIsDropdownOpen(true);
                                        e.target.select();
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setIsDropdownOpen(false), 200);
                                    }}
                                    placeholder="Type to search school..."
                                    className="w-full border border-gray-300 rounded-md p-[10px] text-[14px] md:text-[16px] bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FDC50C]"
                                />
                                {isDropdownOpen && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {statsData.filter(s => s.schoolName.toLowerCase().includes((searchTerm === selectedSchool ? "" : searchTerm).toLowerCase())).map((s) => (
                                            <div 
                                                key={s.schoolName} 
                                                className="p-[10px] hover:bg-gray-100 cursor-pointer text-[14px] md:text-[16px] text-gray-800"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setSelectedSchool(s.schoolName);
                                                    setSearchTerm(s.schoolName);
                                                    setIsDropdownOpen(false);
                                                    if (document.activeElement) {
                                                        document.activeElement.blur();
                                                    }
                                                }}
                                            >
                                                {s.schoolName}
                                            </div>
                                        ))}
                                        {statsData.filter(s => s.schoolName.toLowerCase().includes((searchTerm === selectedSchool ? "" : searchTerm).toLowerCase())).length === 0 && (
                                            <div className="p-[10px] text-gray-500 text-[14px] md:text-[16px]">No matching schools found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {currentSchoolData && (
                            <>
                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[24px] grid grid-cols-2 md:grid-cols-4 gap-[16px]">
                                    <div className="bg-[#FFF4CE] border border-[#FDC50C] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Total Letters Uploaded</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#7A5E00] mt-[4px]">
                                            {currentSchoolData.totalLetters}
                                        </p>
                                    </div>
                                    <div className="bg-[#DBFAE8] border border-[#A1E0BB] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Students Submitted</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#336C4B] mt-[4px]">
                                            {currentSchoolData.studentsSubmitted.length}
                                        </p>
                                    </div>
                                    <div className="bg-[#FFE8E8] border border-[#F4AAAA] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Students Not Submitted</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#A33535] mt-[4px]">
                                            {currentSchoolData.studentsNotSubmitted.length}
                                        </p>
                                    </div>
                                    <div className="bg-[#EAF0FB] border border-[#9BB5E8] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Total Students in School</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#1A3A7A] mt-[4px]">
                                            {currentSchoolData.studentsSubmitted.length + currentSchoolData.studentsNotSubmitted.length}
                                        </p>
                                    </div>
                                </div>

                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[28px] flex flex-wrap gap-[12px]">
                                    <button
                                        id="export-excel-btn"
                                        onClick={exportExcel}
                                        disabled={isRefreshing || isPdfLoading || isExcelLoading}
                                        className="flex items-center gap-[8px] bg-[#217346] text-white rounded-md text-[14px] md:text-[15px] py-[8px] px-[16px] hover:bg-opacity-90"
                                    >
                                        <MdDownload size={18} />
                                        Export Stats as Excel
                                    </button>
                                    <button
                                        id="download-letters-excel-btn"
                                        disabled={isRefreshing || isExcelLoading || isPdfLoading}
                                        onClick={downloadExcelFull}
                                        className="flex items-center gap-[8px] bg-[#1A5276] text-white rounded-md text-[14px] md:text-[15px] py-[8px] px-[16px] hover:bg-opacity-90 disabled:opacity-50"
                                    >
                                        {isExcelLoading ? (
                                            <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                        ) : (
                                            <MdFileDownload size={18} />
                                        )}
                                        Download {roundLabel} Received Letters (Excel)
                                    </button>
                                    <button
                                        id="download-pdf-btn"
                                        disabled={isRefreshing || isPdfLoading || isExcelLoading}
                                        onClick={downloadPdf}
                                        className="flex items-center gap-[8px] bg-[#C0392B] text-white rounded-md text-[14px] md:text-[15px] py-[8px] px-[16px] hover:bg-opacity-90 disabled:opacity-50"
                                    >
                                        {isPdfLoading ? (
                                            <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                        ) : (
                                            <MdFileDownload size={18} />
                                        )}
                                        Download {roundLabel} Received Letters (PDF)
                                    </button>
                                </div>

                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[32px] grid grid-cols-1 xl:grid-cols-2 gap-[24px]">
                                    <div>
                                        <div className="bg-[#FDC50C] rounded-t-xl px-[20px] py-[10px]">
                                            <p className="font-bold text-[14px] md:text-[16px] text-black">
                                                Students Who Submitted ({currentSchoolData.studentsSubmitted.length})
                                            </p>
                                        </div>
                                        <div className="bg-[#FFF4CE] rounded-b-xl">
                                            {currentSchoolData.studentsSubmitted.length === 0 ? (
                                                <p className="text-center text-gray-500 py-[20px] text-[14px]">No submissions yet</p>
                                            ) : (
                                                <div className="hidden md:grid grid-cols-8 px-[20px] py-[8px] border-b border-[#FDC50C] bg-[#FFEBA9]">
                                                    <p className="col-span-3 text-[12px] font-semibold text-gray-700">Name</p>
                                                    <p className="col-span-3 text-[12px] font-semibold text-gray-700">Email</p>
                                                    <p className="col-span-2 text-[12px] font-semibold text-gray-700 text-center">Letters</p>
                                                </div>
                                            )}
                                            {currentSchoolData.studentsSubmitted.map((s, i) => (
                                                <div key={i} className={`px-[20px] py-[10px] ${i < currentSchoolData.studentsSubmitted.length - 1 ? "border-b border-[#FDC50C]" : ""}`}>
                                                    <div className="md:hidden">
                                                        <p className="text-[13px] font-semibold text-gray-800">{s.studentName}</p>
                                                        <p className="text-[11px] text-gray-500">{s.studentEmail}</p>
                                                        <p className="text-[12px] font-bold text-[#7A5E00] mt-[2px]">{s.letterCount} letter{s.letterCount !== 1 ? "s" : ""}</p>
                                                    </div>
                                                    <div className="hidden md:grid grid-cols-8 items-center">
                                                        <p className="col-span-3 text-[13px] text-gray-800">{s.studentName || "—"}</p>
                                                        <p className="col-span-3 text-[12px] text-gray-600 break-all">{s.studentEmail}</p>
                                                        <p className="col-span-2 text-center font-bold text-[14px] text-[#7A5E00]">{s.letterCount}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="bg-[#F4AAAA] rounded-t-xl px-[20px] py-[10px]">
                                            <p className="font-bold text-[14px] md:text-[16px] text-black">
                                                Students Who Have Not Submitted ({currentSchoolData.studentsNotSubmitted.length})
                                            </p>
                                        </div>
                                        <div className="bg-[#FFE8E8] rounded-b-xl">
                                            {currentSchoolData.studentsNotSubmitted.length === 0 ? (
                                                <p className="text-center text-gray-500 py-[20px] text-[14px]">All students have submitted 🎉</p>
                                            ) : (
                                                currentSchoolData.studentsNotSubmitted.map((student, i) => (
                                                    <div key={i} className={`px-[20px] py-[10px] ${i < currentSchoolData.studentsNotSubmitted.length - 1 ? "border-b border-[#F4AAAA]" : ""}`}>
                                                        <p className="text-[13px] md:text-[14px] text-gray-800">{student.studentName || student.studentEmail}</p>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {statsData.length === 0 && !isLoading && (
                            <div className="flex justify-center items-center pt-[60px]">
                                <p className="text-gray-500 text-[16px]">No school data available.</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Dialog
                open={openError}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") handleCloseError();
                }}
                aria-labelledby="school-report-error-title"
                aria-describedby="school-report-error-desc"
                className="backdrop-blur-sm"
            >
                <DialogTitle id="school-report-error-title" className="text-2xl font-bold text-gray-800 border-b border-gray-300 pb-2">
                    Error
                </DialogTitle>
                <DialogContent className="bg-gray-50">
                    <DialogContentText id="school-report-error-desc" className="text-gray-600 text-md mt-2">
                        {errorMsg}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="px-4 py-2">
                    <Button
                        onClick={handleCloseError}
                        style={{ backgroundColor: "#F2AC0E", color: "white", fontWeight: "bold" }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default SchoolLetterReport;
